import { CommonModule } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastModule } from 'primeng/toast';
import { DropdownModule } from 'primeng/dropdown';
import { UserService } from 'src/app/core/services/user.service';
import { USER_TYPES, STATUSES } from 'src/app/shared/constants/common.constants';
import {
  CallDisputeData,
  DisputeStatusResponse,
  DisputeUpdateResponse,
  GenericDispute,
} from 'src/app/shared/model/dispute.model';
import { UserDetailsResponse } from 'src/app/shared/model/user.model';
import { ToasterService } from 'src/app/shared/services/toaster.service';
import { TableModule } from 'primeng/table';
import { domainUrl } from 'src/environments/environment';
import httpErrorCodes from 'src/app/core/common/http_error_codes';
import { DisputeService } from 'src/app/core/services/dispute.service';
import { ButtonModule } from 'primeng/button';
import { CommonService } from 'src/app/shared/services/common.service';

@Component({
  selector: 'app-service-details',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    FormsModule,
    HttpClientModule,
    ToastModule,
    DropdownModule,
    TableModule,
    ButtonModule,
  ],
  providers: [TranslateService],
  templateUrl: './service-details.component.html',
  styleUrls: ['./service-details.component.scss', '../../shared/styles/dispute-details.scss'],
})
export class ServiceDetailsComponent implements OnInit {
  statuses = STATUSES;

  userTypeConstants = USER_TYPES;

  userType: string | null = null;

  baseApiUrl = domainUrl.url;

  callerAvatar: string | null = null;

  talkingBuddyAvatar: string | null = null;

  callId: string | null = null;

  disputeId: string | null = null;

  disputeDetails!: GenericDispute;

  disputeStatusCounts: CallDisputeData['statusCounts'] | null = null;

  disputeStatus: string | null = null;

  disputeUpdateStatus: DisputeUpdateResponse['data'] | null = null;

  disputeStatuses: DisputeStatusResponse['data'] | [] = [];

  disputeComment = '';

  reply = '';

  constructor(
    private userService: UserService,
    private route: ActivatedRoute,
    private translateService: TranslateService,
    private toastService: ToasterService,
    private disputeService: DisputeService,
  ) {}

  ngOnInit(): void {
    this.disputeId = this.route.snapshot.paramMap.get('disputeId');

    if (this.disputeId) {
      this.fetchDisputeData(this.disputeId);
      this.getDisputeStatuses();
    }

    this.disputeDetails.description = '';
  }

  getDisputeStatuses() {
    this.disputeService.getDisputeStatus().subscribe({
      next: (response) => {
        if (response.status === STATUSES.SUCCESS) {
          this.disputeStatuses = response.data;
        }
      },
      error: (err) => {
        if (err.status !== httpErrorCodes.NOT_FOUND) {
          this.toastService.showErrorToast(
            this.translateService.instant(
              'DISPUTE_PAGE.TOAST_MESSAGE.FAILED_TO_LOAD_DISPUTE_STATUS',
            ),
          );
        }
      },
    });
  }

  getConvertedDate(utcDate: string) {
    return CommonService.getFormattedDate(utcDate);
  }

  fetchDisputeData(disputeId: string) {
    this.disputeService.getDisputeById(disputeId).subscribe({
      next: (response) => {
        this.disputeDetails = response.data as unknown as GenericDispute;

        if (this.disputeDetails) {
          this.fetchUserDetails(this.disputeDetails);
        }
      },
      error: (err) => {
        if (err.status !== httpErrorCodes.NOT_FOUND) {
          this.toastService.showErrorToast(
            this.translateService.instant(
              'CALL_DETAILS_PAGE.TOAST_MESSAGE.FAILED_TO_LOAD_DISPUTE_DATA',
            ),
          );
        }
      },
    });
  }

  updateDisputeStatus(dispute: GenericDispute, disputeComment: string) {
    const payload = {
      disputeStatus:
        dispute.disputeStatus === this.statuses.UNDER_REVIEW
          ? this.statuses.UNDER_REVIEW_VALUE
          : dispute.disputeStatus,
      reviewerComment: disputeComment || '',
    };
    this.disputeService.updateDisputeType(dispute.id, payload).subscribe({
      next: (response) => {
        if (response.status === STATUSES.SUCCESS) {
          this.disputeUpdateStatus = response.data;

          if (this.disputeDetails) {
            this.disputeDetails.disputeStatus = this.disputeUpdateStatus.disputeStatus;
          }

          this.fetchDisputeData(this.disputeId || '');
          this.toastService.showSuccessToast(
            this.translateService.instant(
              'CALL_DETAILS_PAGE.TOAST_MESSAGE.UPDATE_DISPUTE_STATUS_SUCCESSFULLY',
            ),
          );
          this.disputeComment = '';
        }
      },
      error: (err) => {
        if (err.status !== httpErrorCodes.NOT_FOUND) {
          this.toastService.showErrorToast(
            this.translateService.instant(
              'CALL_DETAILS_PAGE.TOAST_MESSAGE.FAILED_TO_UPDATE_DISPUTE_STATUS',
            ),
          );
        }
      },
    });
  }

  fetchUserDetails(dispute: GenericDispute) {
    const disputeData = dispute;
    this.userService.getUserDetailsById(dispute.raisedById.toString()).subscribe({
      next: (userResponse: UserDetailsResponse) => {
        const user = userResponse.data;
        this.userType = user.userType;
        disputeData.userType = this.userType;
      },
      error: (err) => {
        if (err.status !== httpErrorCodes.NOT_FOUND) {
          this.toastService.showErrorToast(
            this.translateService.instant(
              'CALL_DETAILS_PAGE.TOAST_MESSAGE.FAILED_TO_LOAD_USER_DATA',
            ),
          );
        }
      },
    });
  }

  formatDuration(duration: string): string {
    if (!duration) {
      return duration;
    }

    const [hours, minutes, seconds] = duration.split(':');
    return parseInt(hours, 10) === 0 ? `${minutes}:${seconds}` : `${hours}:${minutes}:${seconds}`;
  }
}
